import React, { useEffect, useState, useMemo } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import {
  Input,
  Select,
  Button,
  DangerButton,
  SecondaryButton
} from '../styles/TableStyles';
import { mapColumnLabels } from '../styles/ColumnUtils';
import { savedToast, errorToast, deletedToast } from '../utils/toastUtils';
import GroupSelect from '../components/GroupSelect';

const PRODUCT_TYPES = ['Bag', 'Bulk', 'TWB'];

const initialFormState = {
  Style: '',
  Type: '',
  ProductType: '',
  MiscCharge: '',
};

const visibleColumns = mapColumnLabels(Object.keys(initialFormState)).map((col) =>
  col.key === 'MiscCharge' ? { ...col, format: 'currency', center: true } : col
);

const PackTypesPage = () => {
  const [packTypes, setPackTypes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [formData, setFormData] = useState(initialFormState);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'Type', direction: 'asc' });
  const [submitting, setSubmitting] = useState(false);

  const collator = useMemo(() => new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
  }), []);

  useEffect(() => {
    fetchPackTypes();
    fetchGroups();
  }, []);

  const fetchPackTypes = async () => {
    try {
      const res = await authAxios.get('/api/packtypes');
      setPackTypes(res.data);
    } catch (err) {
      console.error('Error fetching PackTypes:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await authAxios.get('/api/packtypes/groups');
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching PackType groups:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const duplicate = packTypes.some(entry =>
      entry.UniqueID !== editingId &&
      entry.Style?.toLowerCase().trim() === String(formData.Style).toLowerCase().trim()
    );
    if (duplicate && !isEditing) {
      alert('Style already exists.');
      return;
    }

    try {
      setSubmitting(true);
      if (isEditing) {
        await authAxios.put(`/api/packtypes/${editingId}`, formData);
      } else {
        await authAxios.post('/api/packtypes', formData);
      }

      setFormData(initialFormState);
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      await Promise.all([fetchPackTypes(), fetchGroups()]);
      savedToast('Pack Type');
    } catch (err) {
      console.error('Error submitting PackType:', err.response?.data || err.message);
      errorToast();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (row) => {
    setFormData({
      Style: row.Style ?? '',
      Type: row.Type ?? '',
      ProductType: row.ProductType ?? '',
      MiscCharge: row.MiscCharge ?? '',
    });
    setEditingId(row.UniqueID);
    setIsEditing(true);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await authAxios.delete(`/api/packtypes/${id}`);
      await Promise.all([fetchPackTypes(), fetchGroups()]);
      deletedToast('Pack Type');
    } catch (err) {
      console.error('Error deleting PackType:', err);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return [...packTypes];
    return [...packTypes].sort((a, b) => {
      const valA = a[sortConfig.key] ?? '';
      const valB = b[sortConfig.key] ?? '';
      return sortConfig.direction === 'asc'
        ? collator.compare(valA, valB)
        : collator.compare(valB, valA);
    });
  }, [packTypes, sortConfig, collator]);

  const filteredData = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return sortedData.filter(row =>
      Object.values(row).some(val =>
        String(val).toLowerCase().includes(q)
      )
    );
  }, [searchQuery, sortedData]);

  return (
    <StandardTableLayout
      title="Pack Types"
      showForm={showForm}
      submitting={submitting}
      onToggleForm={() => {
        setShowForm(!showForm);
        setFormData(initialFormState);
        setIsEditing(false);
        setEditingId(null);
      }}
      onSubmit={handleSubmit}
      columns={visibleColumns}
      formData={formData}
      handleChange={handleChange}
      rows={filteredData}
      editingId={editingId}
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      sortConfig={sortConfig}
      onSort={handleSort}
      renderCell={(col, data, handleChange) => {
        const editable = Boolean(handleChange);
        if (!editable) {
          const v = data?.[col];
          const text = (col === 'MiscCharge' && v !== null && v !== '' && !isNaN(parseFloat(v)))
            ? `$${parseFloat(v).toFixed(2)}`
            : String(v ?? '');
          return (
            <span style={{ display: 'inline-block', padding: '4px 0', textAlign: col === 'MiscCharge' ? 'center' : undefined }}>
              {text}
            </span>
          );
        }
        if (col === 'Type') {
          return (
            <GroupSelect
              value={data.Type}
              groups={groups}
              onChange={(val) => handleChange({ target: { name: 'Type', value: val } })}
            />
          );
        }
        if (col === 'ProductType') {
          return (
            <Select name="ProductType" aria-label="Product Type" value={data.ProductType ?? ''} onChange={handleChange}>
              <option value="">— Product —</option>
              {PRODUCT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          );
        }
        return (
          <Input
            type="text"
            name={col}
            value={data[col] ?? ''}
            onChange={handleChange}
            style={col === 'MiscCharge' ? { textAlign: 'center' } : {}}
          />
        );
      }}
      renderActions={(row) => (
        editingId === row.UniqueID ? (
          <>
            <Button type="submit" disabled={submitting}>Save</Button>
            <SecondaryButton
              type="button"
              onClick={() => {
                setIsEditing(false);
                setEditingId(null);
                setShowForm(false);
                setFormData(initialFormState);
              }}
            >
              Cancel
            </SecondaryButton>
          </>
        ) : (
          <>
            <SecondaryButton type="button" onClick={() => handleEdit(row)}>Edit</SecondaryButton>
            <DangerButton type="button" onClick={() => handleDelete(row.UniqueID)}>Delete</DangerButton>
          </>
        )
      )}
    />
  );
};

export default PackTypesPage;
